import type {
  Logic,
  VacancyFilterKey,
  VacancyOverrideType,
} from "./builder/constants";
import type {
  FormCondition,
  FormRule,
  SegmentFieldsResponse,
} from "./builder/schemas";
import type { SegmentStatus } from "./constants";

import type { OperatorValue } from "@/config/operatorConfig";
import type { FilterOption } from "@/types/FilterOption";

export type SegmentDetailResponse = {
  id: string;
  name: string;
  type: string;
  isInverted: boolean;
  status: SegmentStatus;
  rules: SegmentRule[];
  campaigns: SegmentCampaign[];
  modules: SegmentModule[];
  vacancyOverrides: VacancyOverride[];
  archivedAt: string | null;
};

export type VacancyOverride = {
  vacancyId: number;
  type: VacancyOverrideType;
};

export type SegmentRule = {
  id: string;
  index: number;
  name: string;
  type: string;
  isInverted: boolean;
  conditions: RuleCondition[];
};

export type RuleCondition = {
  id: string;
  index: number;
  field: string;
  operator: string;
  value?: string | null;
  values?: string[] | null;
  isInverted: boolean;
};

export type SegmentCampaign = {
  id: string;
  name: string;
  status: SegmentStatus;
  jobBoards: { integrationName: string }[];
};

export type SegmentModule = {
  clientIntegrationModuleId: string;
  moduleDisplayName: string;
  integrationName: string;
  isActive: boolean;
};

export type SegmentVacancyPreviewResponse = {
  totalCount: number;
  preview: SegmentVacancy[];
};

export type SegmentVacancy = {
  vacancyId: number;
  externalId: string | null;
  title: string | null;
  addedAt: string | null;
};

export const SEGMENT_CHANGE_TYPE = {
  SEGMENT_CREATED: "SegmentCreated",
  RULES_CHANGED: "RulesChanged",
  TYPE_CHANGED: "TypeChanged",
  INVERSION_CHANGED: "InversionChanged",
  VACANCIES_ADDED: "VacanciesAdded",
  VACANCIES_REMOVED: "VacanciesRemoved",
  OVERRIDES_CHANGED: "OverridesChanged",
  SEGMENT_ARCHIVED: "SegmentArchived",
  SEGMENT_ADDED_TO_CAMPAIGN: "SegmentAddedToCampaign",
  SEGMENT_REMOVED_FROM_CAMPAIGN: "SegmentRemovedFromCampaign",
  CAMPAIGN_CREATED: "CampaignCreated",
  CAMPAIGN_INTEGRATION_CREATED: "CampaignIntegrationCreated",
} as const;

export type SegmentChangeType =
  (typeof SEGMENT_CHANGE_TYPE)[keyof typeof SEGMENT_CHANGE_TYPE];

const SEGMENT_CHANGE_TYPES: ReadonlySet<string> = new Set(
  Object.values(SEGMENT_CHANGE_TYPE),
);

export const isSegmentChangeType = (
  value: string,
): value is SegmentChangeType => SEGMENT_CHANGE_TYPES.has(value);

/** Payload of every `segment:*` realtime event (`SegmentEventPayload` on the backend). */
export type SegmentRealtimeEvent = {
  segmentId: string;
};

export type SegmentActivity = {
  changeType: string;
  source: string;
  entityName: string | null;
  changes: unknown;
  occurredAt: string;
};

export type SaveCondition = Omit<
  RuleCondition,
  "id" | "operator" | "value" | "values"
> & {
  id?: string;
  operator: OperatorValue;
  values: string[];
};

export type SaveRule = Omit<SegmentRule, "id" | "type" | "conditions"> & {
  id?: string;
  type: Logic;
  conditions: SaveCondition[];
};

export type SaveSegmentRequest = Pick<
  SegmentDetailResponse,
  "name" | "isInverted"
> & {
  type: Logic;
  clientId2?: string;
  rules: SaveRule[];
  vacancyOverrides: VacancyOverride[];
};

export type SegmentVacancyCard = {
  vacancyId: number;
  title: string | null;
  field: string | null;
  hoursPerWeekMin: string | null;
  hoursPerWeekMax: string | null;
  contractHours: string | null;
  city: string | null;
  company: string | null;
  country: string | null;
};

export type SegmentVacancyItem = {
  vacancy: SegmentVacancyCard;
  matchesRules: boolean;
};

export type SegmentVacanciesResponse = {
  items: SegmentVacancyItem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type SegmentVacancyFiltersResponse = Record<
  VacancyFilterKey,
  Required<Omit<FilterOption<string>, "badge">>[]
>;

export type SegmentVacancyListCounts = {
  includedCount: number;
  excludedCount: number;
};

export type OverrideImpactItem = Pick<VacancyOverride, "type"> & {
  vacancy: SegmentVacancyCard;
  isLive: boolean;
  isUnpublished: boolean;
  matchesSavedRules: boolean | null;
  matchesDraftRules: boolean;
};

export type ConditionValue = string | string[];

export type ConditionBadgeParts = {
  field: string;
  operator?: string;
  value?: ConditionValue;
  markExcluded: boolean;
};

export type SegmentField = Omit<
  SegmentFieldsResponse[number],
  "field" | "labelKey"
> & {
  value: string;
  label: string;
};

export type OverlapMark = Pick<OverlapGroup, "field"> & {
  groupKey: OverlapGroup["key"];
};

export type OverlapGroup = Pick<FormCondition, "field"> & {
  key: string;
  isNegative: boolean;
  members: {
    ruleKey: FormRule["clientKey"];
    conditionKey: FormCondition["clientKey"];
  }[];
  valueChunks: FormCondition["values"][];
};

export type OverlapSource = {
  ruleKey: FormRule["clientKey"];
  condition: FormCondition;
};

export type ConditionOverlap = Pick<OverlapMark, "field"> & {
  isHighlighted: boolean;
  onHighlightChange: (isHighlighted: boolean) => void;
};

export type RuleOverlap = {
  byCondition: Map<FormCondition["clientKey"], OverlapMark>;
  highlightedGroupKey: OverlapGroup["key"] | null;
  onHighlight: (key: OverlapGroup["key"] | null) => void;
};
